import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Dialog, DialogContent } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import {
  Search, Briefcase, Users, UserPlus, FileText, Receipt,
  Presentation, ClipboardList, ScrollText, User, HardHat,
  Box, Package, Wrench, Image, Layout, Bell, Clock, X,
} from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";

const TYPE_CONFIG = {
  project:      { icon: Briefcase,     label: "Projects",     color: "var(--app-blue)",  shortcut: "project" },
  proposal:     { icon: Presentation,  label: "Proposals",    color: "var(--app-purple)",  shortcut: "proposal" },
  client:       { icon: Users,         label: "Clients",      color: "var(--app-green)",  shortcut: "client" },
  lead:         { icon: UserPlus,      label: "Leads",        color: "var(--app-amber)",  shortcut: "lead" },
  quote:        { icon: ClipboardList, label: "Quotes",       color: "var(--app-pink)",  shortcut: "quote" },
  brief:        { icon: ScrollText,    label: "Briefs",       color: "var(--app-indigo)",  shortcut: "brief" },
  contract:     { icon: FileText,      label: "Contracts",    color: "var(--app-teal)",  shortcut: "contract" },
  invoice:      { icon: Receipt,       label: "Invoices",     color: "var(--app-red)",  shortcut: "invoice" },
  user:         { icon: User,          label: "Team",         color: "#0EA5E9",  shortcut: "team" },
  crew:         { icon: HardHat,       label: "Crew",         color: "#A855F7",  shortcut: "crew" },
  catalog:      { icon: Box,           label: "Catalog",      color: "#F97316",  shortcut: "catalog" },
  package:      { icon: Package,       label: "Packages",     color: "var(--app-cyan)",  shortcut: "package" },
  equipment:    { icon: Wrench,        label: "Equipment",    color: "#84CC16",  shortcut: "equipment" },
  gallery:      { icon: Image,         label: "Galleries",    color: "#F472B6",  shortcut: "gallery" },
  template:     { icon: Layout,        label: "Templates",    color: "#A78BFA",  shortcut: "template" },
  notification: { icon: Bell,          label: "Activity",     color: "var(--app-text-muted)",  shortcut: "activity" },
  purchase_order: { icon: ClipboardList, label: "POs", color: "var(--app-amber)", shortcut: "po" },
};

const FILTER_ENTRIES = Object.entries(TYPE_CONFIG).map(([key, cfg]) => ({ key, ...cfg }));

export function GlobalSearch() {
  const { language, t, ti } = useLanguage();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [activeFilter, setActiveFilter] = useState(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const debounceRef = useRef(null);

  // Recent searches — persisted in localStorage
  const STORAGE_KEY = "bk_recent_searches";
  const MAX_RECENT = 8;
  const [recentSearches, setRecentSearches] = useState(() => {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]"); } catch { return []; }
  });

  const addRecentSearch = useCallback((item) => {
    setRecentSearches(prev => {
      const filtered = prev.filter(r => !(r.id === item.id && r.type === item.type));
      const updated = [{ id: item.id, type: item.type, title: item.title, subtitle: item.subtitle, url: item.url }, ...filtered].slice(0, MAX_RECENT);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      return updated;
    });
  }, []);

  const removeRecent = useCallback((idx) => {
    setRecentSearches(prev => {
      const updated = prev.filter((_, i) => i !== idx);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      return updated;
    });
  }, []);

  const clearAllRecent = useCallback(() => {
    setRecentSearches([]);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  // CMD+K shortcut
  useEffect(() => {
    const down = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setOpen(prev => !prev);
      }
    };
    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  // Reset on open
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 100);
      setQuery("");
      setResults([]);
      setSelectedIdx(0);
      setActiveFilter(null);
    }
  }, [open]);

  // Parse @filter prefix from query
  const { searchQuery, filterSuggestions, showFilterHints } = useMemo(() => {
    if (!query.startsWith("@")) return { searchQuery: query, filterSuggestions: [], showFilterHints: false };

    const spaceIdx = query.indexOf(" ");
    if (spaceIdx === -1) {
      // Still typing the filter — show suggestions
      const partial = query.slice(1).toLowerCase();
      const matches = FILTER_ENTRIES.filter(f => f.shortcut.startsWith(partial) || f.label.toLowerCase().startsWith(partial));
      return { searchQuery: "", filterSuggestions: matches, showFilterHints: true };
    }

    // Filter is locked in, extract the real search query
    const filterText = query.slice(1, spaceIdx).toLowerCase();
    const matched = FILTER_ENTRIES.find(f => f.shortcut === filterText || f.label.toLowerCase() === filterText);
    return { searchQuery: query.slice(spaceIdx + 1).trim(), filterSuggestions: [], showFilterHints: false, matchedFilter: matched };
  }, [query]);

  // Extract matchedFilter for use in search
  const matchedFilter = useMemo(() => {
    if (activeFilter) return activeFilter;
    if (!query.startsWith("@")) return null;
    const spaceIdx = query.indexOf(" ");
    if (spaceIdx === -1) return null;
    const filterText = query.slice(1, spaceIdx).toLowerCase();
    return FILTER_ENTRIES.find(f => f.shortcut === filterText || f.label.toLowerCase() === filterText) || null;
  }, [query, activeFilter]);

  // Debounced search
  const doSearch = useCallback(async (q, filter) => {
    if (q.length < 2) { setResults([]); setLoading(false); return; }
    setLoading(true);
    try {
      const params = new URLSearchParams({ q });
      if (filter) params.append("type", filter.key);
      const res = await api.get(`/search?${params.toString()}`);
      setResults(res.data.results || []);
    } catch (err) {
      console.error("Global search error:", err);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleInput = (val) => {
    setQuery(val);
    setSelectedIdx(0);

    // If user clears the filter prefix, reset activeFilter
    if (!val.startsWith("@") && activeFilter) setActiveFilter(null);

    if (debounceRef.current) clearTimeout(debounceRef.current);

    // Parse and search
    const spaceIdx = val.indexOf(" ");
    if (val.startsWith("@") && spaceIdx > 0) {
      const filterText = val.slice(1, spaceIdx).toLowerCase();
      const filter = FILTER_ENTRIES.find(f => f.shortcut === filterText || f.label.toLowerCase() === filterText);
      const searchPart = val.slice(spaceIdx + 1).trim();
      if (filter) setActiveFilter(filter);
      debounceRef.current = setTimeout(() => doSearch(searchPart, filter), 250);
    } else if (!val.startsWith("@")) {
      debounceRef.current = setTimeout(() => doSearch(val, activeFilter), 250);
    }
  };

  const applyFilter = (filter) => {
    setActiveFilter(filter);
    setQuery(`@${filter.shortcut} `);
    setSelectedIdx(0);
    inputRef.current?.focus();
  };

  const clearFilter = () => {
    setActiveFilter(null);
    setQuery("");
    setResults([]);
    inputRef.current?.focus();
  };

  const handleSelect = (item) => {
    addRecentSearch(item);
    setOpen(false);
    setQuery("");
    setResults([]);
    setActiveFilter(null);
    navigate(item.url);
  };

  // Keyboard nav
  const handleKeyDown = (e) => {
    if (showFilterHints && filterSuggestions.length > 0) {
      if (e.key === "ArrowDown") { e.preventDefault(); setSelectedIdx(prev => Math.min(prev + 1, filterSuggestions.length - 1)); }
      else if (e.key === "ArrowUp") { e.preventDefault(); setSelectedIdx(prev => Math.max(prev - 1, 0)); }
      else if (e.key === "Enter" || e.key === "Tab") { e.preventDefault(); applyFilter(filterSuggestions[selectedIdx]); }
      else if (e.key === "Escape") setOpen(false);
      return;
    }
    if (e.key === "ArrowDown") { e.preventDefault(); setSelectedIdx(prev => Math.min(prev + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setSelectedIdx(prev => Math.max(prev - 1, 0)); }
    else if (e.key === "Enter" && results[selectedIdx]) { e.preventDefault(); handleSelect(results[selectedIdx]); }
    else if (e.key === "Escape") setOpen(false);
    else if (e.key === "Backspace" && query === "" && activeFilter) clearFilter();
  };

  // Group results by type
  const grouped = {};
  results.forEach(r => { if (!grouped[r.type]) grouped[r.type] = []; grouped[r.type].push(r); });

  let flatIdx = 0;

  return (
    <>
      <button onClick={() => setOpen(true)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors"
        style={{ background: "var(--app-surface)", border: "1px solid var(--app-border)", color: "var(--app-text-muted)" }}
        data-testid="global-search-trigger">
        <Search className="w-3.5 h-3.5" />
        <span className="hidden sm:inline">{t("Search...")}</span>
        <kbd className="hidden sm:inline-flex h-5 select-none items-center gap-1 rounded border border-current/20 bg-current/5 px-1.5 font-mono text-[10px] font-medium opacity-60">
          <span className="text-xs">&#x2318;</span>K
        </kbd>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="p-0 overflow-hidden max-w-lg" style={{ background: "var(--app-bg)" }}>
          {/* Search Input */}
          <div className="flex items-center gap-2 px-3 border-b" style={{ borderColor: "var(--app-border)" }}>
            <Search className="w-4 h-4 shrink-0" style={{ color: "var(--app-text-muted)" }} />
            {matchedFilter && !showFilterHints && (
              <button onClick={clearFilter} className="flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold shrink-0 transition-colors hover:opacity-80"
                style={{ background: `${matchedFilter.color}20`, color: matchedFilter.color }}
                data-testid="active-filter-badge">
                {(() => { const I = matchedFilter.icon; return <I className="w-3 h-3" />; })()}
                {matchedFilter.label}
                <span className="opacity-60 ml-0.5">&times;</span>
              </button>
            )}
            <Input ref={inputRef} value={query} onChange={e => handleInput(e.target.value)} onKeyDown={handleKeyDown}
              placeholder={matchedFilter ? ti(`Search ${matchedFilter.label.toLowerCase()}...`, `Buscar ${matchedFilter.label.toLowerCase()}...`) : t("Search everything... (type @ to filter)")}
              className="border-0 shadow-none focus-visible:ring-0 h-11 text-sm"
              style={{ background: "transparent", color: "var(--app-text)" }}
              data-testid="global-search-input" />
            {loading && <div className="w-4 h-4 border-2 border-t-transparent rounded-full animate-spin shrink-0" style={{ borderColor: "var(--app-accent)", borderTopColor: "transparent" }} />}
          </div>

          <div className="max-h-[320px] overflow-y-auto p-1" data-testid="global-search-results">
            {/* Filter suggestions when typing @ */}
            {showFilterHints && filterSuggestions.length > 0 && (
              <div>
                <div className="px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wider" style={{ color: "var(--app-text-muted)" }}>
                  {t("Filter by category")}
                </div>
                {filterSuggestions.map((f, i) => {
                  const Icon = f.icon;
                  return (
                    <button key={f.key} onClick={() => applyFilter(f)}
                      className="w-full text-left px-3 py-2 rounded-md flex items-center gap-3 transition-colors"
                      style={{ background: i === selectedIdx ? "var(--app-surface)" : "transparent", color: "var(--app-text)" }}
                      onMouseEnter={() => setSelectedIdx(i)}
                      data-testid={`filter-option-${f.key}`}>
                      <Icon className="w-4 h-4" style={{ color: f.color }} />
                      <span className="text-sm font-medium">@{f.shortcut}</span>
                      <span className="text-xs" style={{ color: "var(--app-text-muted)" }}>{f.label}</span>
                      <kbd className="ml-auto text-[10px] px-1.5 py-0.5 rounded" style={{ background: "var(--app-surface)", color: "var(--app-text-faint)" }}>Tab</kbd>
                    </button>
                  );
                })}
              </div>
            )}

            {/* Empty state — Recent searches + filter chips */}
            {!showFilterHints && query.length < 2 && !matchedFilter && (
              <div className="py-3 px-2 space-y-3">
                {/* Recent searches */}
                {recentSearches.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between px-1 mb-1.5">
                      <span className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: "var(--app-text-muted)" }}>
                        <Clock className="w-3 h-3 inline mr-1" />{t("Recent")}
                      </span>
                      <button onClick={clearAllRecent} className="text-[10px] hover:underline" style={{ color: "var(--app-text-muted)" }}
                        data-testid="clear-recent-searches">{t("Clear all")}</button>
                    </div>
                    {recentSearches.map((item, i) => {
                      const cfg = TYPE_CONFIG[item.type] || { icon: FileText, label: item.type, color: "var(--app-text-muted)" };
                      const Icon = cfg.icon;
                      return (
                        <div key={`recent-${i}`} className="flex items-center group">
                          <button onClick={() => { addRecentSearch(item); setOpen(false); navigate(item.url); }}
                            className="flex-1 text-left px-3 py-1.5 rounded-md flex items-center gap-2.5 transition-colors hover:bg-[var(--app-surface)]"
                            data-testid={`recent-search-${i}`}>
                            <Icon className="w-3.5 h-3.5 shrink-0" style={{ color: cfg.color }} />
                            <span className="text-xs font-medium truncate" style={{ color: "var(--app-text)" }}>{item.title}</span>
                            {item.subtitle && <span className="text-[10px] truncate" style={{ color: "var(--app-text-muted)" }}>{item.subtitle}</span>}
                            <span className="ml-auto text-[9px] px-1.5 py-0.5 rounded shrink-0" style={{ background: `${cfg.color}12`, color: cfg.color }}>{cfg.label}</span>
                          </button>
                          <button onClick={(e) => { e.stopPropagation(); removeRecent(i); }}
                            className="p-1 rounded opacity-0 group-hover:opacity-60 hover:opacity-100 transition-opacity shrink-0"
                            data-testid={`remove-recent-${i}`}>
                            <X className="w-3 h-3" style={{ color: "var(--app-text-muted)" }} />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Filter chips */}
                <div className="text-center">
                  <p className="text-[10px] mb-1.5" style={{ color: "var(--app-text-muted)" }}>
                    {t("Type to search, or use @ filters")}
                  </p>
                  <div className="flex flex-wrap gap-1.5 justify-center">
                    {FILTER_ENTRIES.slice(0, 8).map(f => (
                      <button key={f.key} onClick={() => applyFilter(f)}
                        className="px-2 py-1 rounded-md text-[10px] font-medium transition-colors hover:opacity-80"
                        style={{ background: `${f.color}12`, color: f.color }}
                        data-testid={`quick-filter-${f.key}`}>
                        @{f.shortcut}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* No results */}
            {!showFilterHints && ((searchQuery || query).length >= 2 || matchedFilter) && !loading && results.length === 0 && query.length >= 2 && (
              <p className="text-center py-8 text-sm" style={{ color: "var(--app-text-muted)" }}>
                {ti(`No results found for "${searchQuery || query}"`, `No se encontraron resultados para "${searchQuery || query}"`)}
              </p>
            )}

            {/* Results grouped by type */}
            {!showFilterHints && Object.entries(grouped).map(([type, items]) => {
              const cfg = TYPE_CONFIG[type] || { icon: FileText, label: type, color: "var(--app-text-muted)" };
              const Icon = cfg.icon;
              return (
                <div key={type} className="mb-1">
                  <div className="px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wider flex items-center gap-1.5" style={{ color: cfg.color }}>
                    <Icon className="w-3 h-3" />{cfg.label}
                  </div>
                  {items.map(item => {
                    const idx = flatIdx++;
                    return (
                      <button key={`${item.type}-${item.id}`} onClick={() => handleSelect(item)}
                        className="w-full text-left px-3 py-2 rounded-md flex items-center gap-3 transition-colors"
                        style={{ background: idx === selectedIdx ? "var(--app-surface)" : "transparent", color: "var(--app-text)" }}
                        onMouseEnter={() => setSelectedIdx(idx)}
                        data-testid={`search-result-${item.id}`}>
                        <Icon className="w-4 h-4 shrink-0 opacity-50" />
                        <div className="flex flex-col min-w-0">
                          <span className="text-sm font-medium truncate">{item.title}</span>
                          {item.subtitle && <span className="text-xs truncate" style={{ color: "var(--app-text-muted)" }}>{item.subtitle}</span>}
                        </div>
                        <span className="ml-auto text-[10px] shrink-0 px-1.5 py-0.5 rounded" style={{ background: `${cfg.color}15`, color: cfg.color }}>
                          {cfg.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </div>

          {/* Footer hints */}
          <div className="flex items-center justify-between px-3 py-1.5 border-t text-[10px]" style={{ borderColor: "var(--app-border)", color: "var(--app-text-faint)" }}>
            <span>@ {t("Filter")}</span>
            <span>&#x2191;&#x2193; {t("Navigate")}</span>
            <span>&#x21B5; {t("Open")}</span>
            <span>ESC {t("Close")}</span>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
